<template>
  <div class="nominations" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header">{{ organizationDetails.programName }}</h1>
    </div>

    <div class="form-container">
      <!-- Nomination submitted message -->
      <div v-if="nominationSubmitted" class="nomination-submitted status-message card-shape">
        <div class="success-circle-check-icon">
          <img src="@/assets/icons/success-circle-check-icon.png" />
        </div>
        <div>
          <h4 v-translate>Nomination Submitted</h4>
          <p v-translate>
            Your nomination has been sent for internal review by Canary. Nominees will be notified 
            within 2 business days if they are eligible to fill out an application.
          </p>
        </div>
      </div>

      <!-- Nomination saved message -->
      <div v-if="nominationSaved" class="nomination-saved status-message card-shape">
        <h4 v-translate>Nomination Saved</h4>
        <p v-translate>
          Your nomination details have been saved. You may return to finalize this nomination at a
          later date, by clicking on the nominee's name below.
        </p>
      </div>

      <div class="cta-section-wrapper">
        <!-- Add a nominee CTA -->
        <div class="add-a-nominee cta-section">
          <h2 v-translate>Add a nominee</h2>
          <h4 v-translate>
            Fill out a quick form to nominate someone from your commmunity that meets the Upward
            Fund's hardship guidelines. Canary will review your nomination and if they meet criteria, 
            will reach out to them for an application.
          </h4>
          <button @click="addNomination" class="form-button form-button-160">
            <span v-translate>Add</span>
          </button>
        </div>

        <!-- Nominations container -->
        <div class="nominations cta-section card-shape">
          <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
          <div v-if="!loading" class="nominations-list">
            <!-- Nominations list header -->
            <div class="nominations-list-header">
              <div v-translate>Name</div>
              <div v-translate>Date Nominated</div>
              <div v-translate>Status</div>
            </div>

            <!-- Nominations list -->
            <template v-if="nominations && nominations.length > 0">
              <div v-for="nomination in nominations" class="nomination">
                <div>
                  <!-- If the survey is incomplete, then the full name is a link to the survey response -->
                  <router-link v-if="nomination.status == 'INCOMPLETE'" :to="nomination.id" append>
                    {{ nomination.full_name ? nomination.full_name : 'N/A' }}
                  </router-link>
                  <!-- Else, then the just display the full name as text -->
                  <template v-else>
                    {{ nomination.full_name ? nomination.full_name : 'N/A' }}
                  </template>
                </div>
                <div>{{ nomination.date }}</div>
                <div>{{ nomination.status }}</div>
              </div>
            </template>

            <!-- No nominations -->
            <template v-else>
              <div class="form-field-container no-nominations" v-translate>
                You have not started any nominations yet, click "Add" to get started.
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner } from '@/components'

import NominationsApi from '@/services/nominations'

export default {
  name: 'ThirdParyHome',
  components: {
    LoadSpinner,
  },
  props: {
    nominationSubmitted: {
      type: Boolean,
      default: false,
    },
    nominationSaved: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      nominations: null,
    }
  },
  async mounted() {
    // Retrieve all nominations for the current user from the Nominations API service
    // and update our model
    var nominations = await NominationsApi.list()
    this.nominations = nominations
    this.loading = false
  },
  methods: {
    async addNomination() {
      // Create a new nomination, then redirect the nomination survey
      let nominationId = await NominationsApi.create()
      this.$router.push({ name: 'Nomination', params: { id: nominationId } })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.nominations {
  .form-container {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .card-shape {
    &.status-message {
      display: flex;
      width: 60%;
    }

    &.nomination-submitted {
      flex-direction: row;
      background-color: #e6ffd9;
    }

    &.nomination-saved {
      flex-direction: column;
      background-color: #fcfaf7;
    }

    .success-circle-check-icon {
      margin-right: 1rem;
    }
  }

  .cta-section-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    margin: 0rem 1rem;
    width: 100%;

    @include desktop-screen {
      flex-direction: row;
    }

    .cta-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      margin-top: 2.5rem;
      align-self: center;
      max-width: 90%;
      justify-content: space-between;

      @include desktop-screen {
        align-self: top;
      }

      h1 {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 2rem;
      }

      h4 {
        font-size: 1.25rem;
        line-height: 1.75rem;
        margin-bottom: 2rem;
      }

      h5 {
        margin-bottom: 0;

        span {
          font-weight: 500;
          font-size: 1rem;
          line-height: 1.5rem;
        }
      }
    }
  }

  .nominations {
    width: 800px;

    @include tablet-screen {
      width: 100%;
      max-width: unset !important;
    }

    .no-nominations {
      margin-top: 1rem;
    }
  }

  .add-a-nominee {
    max-width: 400px !important;
    justify-self: center;
    align-self: center;
  }

  .nominations-list {
    width: 100%;

    .nominations-list-header,
    .nomination {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 0.5rem;

      &.nominations-list-header {
        border-bottom: 2px solid #dfdfe0;

        div {
          font-weight: bold;
        }
      }

      div:nth-child(1) {
        width: 30%;
      }

      div:nth-child(2) {
        width: 35%;
      }

      div:nth-child(3) {
        width: 20%;
        overflow-wrap: break-word;
      }
    }
  }
}
</style>
